/**
 * Page-load gating predicates for the Analysis Dashboard.
 *
 * Heavy periods (e.g. FY, >=16w) trigger multi-minute DuckDB scans, so the
 * dashboard avoids speculative prefetch of inactive component-combinations tabs
 * on them, and defers filter-counts / country-summary until stats+rates land.
 *
 * Extracted as pure functions so the behaviour is unit-testable without
 * rendering the (very large) Dashboard component.
 */

/**
 * Whether a component-combinations tab should be enabled (i.e. its data fetched).
 *
 * The active tab always loads. Inactive tabs only prefetch on light periods once
 * the initial active-tab data has arrived; on heavy periods they stay disabled
 * and fetch lazily when the user actually opens them.
 */
export function shouldPrefetchTab(
    isActiveTab: boolean,
    heavyPeriod: boolean,
    initialLoadComplete: boolean
): boolean {
    return isActiveTab || (!heavyPeriod && initialLoadComplete);
}

/**
 * Whether heavy secondary dashboard fetches (filter-counts, country-summary, …)
 * should wait for core KPI data (stats + rates).
 *
 * Important: when the filter key changes, coreLoaded must already be false in
 * the same render — otherwise useApi fires once with the new country before the
 * defer gate engages (double identical /v1/filter-counts).
 */
export function shouldDeferHeavySecondary(heavyPeriod: boolean, coreLoaded: boolean): boolean {
    return heavyPeriod && !coreLoaded;
}

/**
 * Whether frame_types for API calls are resolved enough to fire heavy fetches.
 *
 * With no URL param, the UI eventually uses anchorConfig.default_frame_types
 * (e.g. PAX → regular only). Before config loads it would fall back to all
 * categories (regular,corner,end,mid). Firing both doubles FY stats cost
 * (~70s + ~70s observed in prod).
 *
 * - Explicit URL frame_types → ready immediately
 * - Implicit default → wait until anchor-config for this anchor is loaded
 */
export function isFrameTypesReady(
    frameTypesFromUrl: string[] | null,
    anchorConfigLoadedForAnchor: boolean
): boolean {
    if (frameTypesFromUrl !== null) return true;
    return anchorConfigLoadedForAnchor;
}

/** Only start Parquet-backed requests after a successful budget preflight. */
export function canLoadParquetQueries(
    selectionReady: boolean,
    estimateLoading: boolean,
    estimateAllowed: boolean | undefined,
    selectionKey: string,
    estimateKey: string | undefined
): boolean {
    return selectionReady && !estimateLoading && estimateAllowed === true && estimateKey === selectionKey;
}
